import { useState } from "react";
import { api } from "../lib/api";

const ROLES = ["Landowner", "Family or trust representative", "Agent or broker", "County official",
  "School or church", "Club official", "Other"];

/**
 * File the exact plot from wherever the reader is standing. It lands in the same intake queue as every other
 * referral, tagged as land, with the county figures already attached so nobody has to retype them.
 */
export const LandLead = ({ county, acres, location, locationLabel, figures = "" }) => {
  const [form, setForm] = useState({
    your_name: "", your_email: "", your_role: "Landowner", subject_name: "", site_location: "",
    tenure: "", timeline: "", notes: "",
  });
  const [state, setState] = useState({ status: "idle" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setState({ status: "sending" });
    try {
      const r = await api.post("/intake/referral", {
        ...form,
        referral_type: "Land for a football project",
        county,
        land_size: `${acres} acres`,
        capacity: locationLabel,
        notes: [form.notes, `Priced on the lease comparison page: ${acres} acres in ${county}, ${locationLabel}`,
          figures].filter(Boolean).join("\n"),
      });
      setState({ status: "done", reference: r.data.reference });
    } catch {
      setState({ status: "error" });
    }
  };

  if (state.status === "done") {
    return (
      <div className="border border-ochre/40 bg-[#0b0b0b] p-7" data-testid="land-lead-done">
        <p className="mono text-[0.6rem] uppercase tracking-[0.16em] text-ochre">Filed</p>
        <p className="mt-3 font-display text-2xl uppercase tracking-tight text-white">
          {state.reference}
        </p>
        <p className="mt-3 max-w-xl text-sm leading-relaxed text-zinc-400">
          Your plot is in the queue with the {county} figures attached. Quote that reference in any email about
          it. If it fits a project we are taking to partners, we come back to you.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="grid gap-5 bg-[#0b0b0b] p-7 pb-24 md:grid-cols-2 md:pb-7" data-testid="land-lead-form">
      {[["your_name", "Your name", true], ["your_email", "Your email", true],
        ["subject_name", "What the plot is called or known as", true],
        ["site_location", "Where it is — ward, road, landmark", false],
        ["tenure", "Tenure — title deed, allotment, family land, county land", false],
        ["timeline", "When it could be available", false]].map(([key, label, req]) => (
        <label key={key} className="block">
          <span className="mono block text-[0.6rem] uppercase tracking-[0.16em] text-zinc-400">{label}</span>
          <input
            value={form[key]}
            onChange={set(key)}
            required={req}
            type={key === "your_email" ? "email" : "text"}
            data-testid={`land-lead-${key.replace(/_/g, "-")}`}
            className="mt-2 w-full border border-white/20 bg-transparent px-3.5 py-2.5 text-sm text-white focus:border-ochre focus:outline-none"
          />
        </label>
      ))}
      <label className="block">
        <span className="mono block text-[0.6rem] uppercase tracking-[0.16em] text-zinc-400">You are the</span>
        <select
          value={form.your_role}
          onChange={set("your_role")}
          data-testid="land-lead-your-role"
          className="mt-2 w-full border border-white/20 bg-black px-3.5 py-2.5 text-sm text-white focus:border-ochre focus:outline-none"
        >
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </label>
      <label className="block md:col-span-2">
        <span className="mono block text-[0.6rem] uppercase tracking-[0.16em] text-zinc-400">
          Anything else about the plot
        </span>
        <textarea
          value={form.notes}
          onChange={set("notes")}
          rows={3}
          data-testid="land-lead-notes"
          className="mt-2 w-full border border-white/20 bg-transparent px-3.5 py-2.5 text-sm text-white focus:border-ochre focus:outline-none"
        />
      </label>
      <p className="mono md:col-span-2 text-[0.6rem] uppercase leading-relaxed tracking-[0.14em] text-zinc-500"
         data-testid="land-lead-note">
        Filed as {acres} acres in {county} County, {locationLabel.toLowerCase()}, with the figures on this page
        attached. Nothing is published — this reaches the review queue only.
      </p>
      <div className="md:col-span-2 flex flex-wrap items-center gap-4">
        <button
          type="submit"
          disabled={state.status === "sending"}
          data-testid="land-lead-submit"
          className="mono border border-ochre px-6 py-3 text-[0.6rem] uppercase tracking-[0.16em] text-ochre transition-colors hover:bg-ochre hover:text-black disabled:opacity-50"
        >
          {state.status === "sending" ? "Filing…" : "File this plot"}
        </button>
        {state.status === "error" ? (
          <span className="mono text-[0.6rem] uppercase tracking-[0.16em] text-maasai" data-testid="land-lead-error">
            That did not send. Try again, or email media@kickstartkenyafootball.com.
          </span>
        ) : null}
      </div>
    </form>
  );
};
